"use client";
import { useSpring, animated, useInView, useSprings } from '@react-spring/web';
import { Waves, Trees, Volume2, BookOpenCheck, Users, Zap, MapPin } from 'lucide-react';
import ImageCarousel from '../ui/ImageCarousel';
import { useEffect } from 'react';
import { trackViewContent } from '../PixelTracker';

export default function VenueSection() {
  const images = [
    { src: "/AMAROO/IMG_6896.webp", alt: "Instalaciones del retiro Amaroo en Alicante - Espacios amplios para dinámicas grupales de coaching" }, // 17th image now first
    { src: "/AMAROO/IMG_6877.webp", alt: "Centro de retiros Amaroo Alicante - Sede exclusiva certificación Coaching X Valores" },
    { src: "/AMAROO/IMG_6878.webp", alt: "Espacios de meditación y reflexión en Amaroo - Retiro coaching transformacional Alicante" },
    { src: "/AMAROO/IMG_6879.webp", alt: "Jardines mediterráneos del centro Amaroo - Entorno natural para formación en coaching" },
    { src: "/AMAROO/IMG_6880.webp", alt: "Áreas de descanso al aire libre en Amaroo - Certificación internacional coaching valores" },
    { src: "/AMAROO/IMG_6881.webp", alt: "Salas de formación con luz natural en Amaroo - Aulas para certificación coaching profesional" },
    { src: "/AMAROO/IMG_6882.webp", alt: "Espacios colaborativos para trabajo en grupo - Formación coaching con valores Alicante" },
    { src: "/AMAROO/IMG_6883.webp", alt: "Naturaleza del centro Amaroo que inspira transformación personal y profesional" },
    { src: "/AMAROO/IMG_6884.webp", alt: "Instalaciones modernas y acogedoras de Amaroo - Centro de excelencia en coaching" },
    { src: "/AMAROO/IMG_6885.webp", alt: "Zonas de práctica coaching al aire libre - Experiencia inmersiva certificación valores" },
    { src: "/AMAROO/IMG_6886.webp", alt: "Comedor con vistas panorámicas en Amaroo - Alimentación consciente retiro coaching" },
    { src: "/AMAROO/IMG_6887.webp", alt: "Habitaciones confortables del centro Amaroo - Alojamiento incluido certificación coaching" },
    { src: "/AMAROO/IMG_6891.webp", alt: "Espacios de conexión y networking profesional - Comunidad coaches certificados valores" },
    { src: "/AMAROO/IMG_6892.webp", alt: "Terrazas con vistas a la montaña en Amaroo - Entorno inspirador coaching transformacional" },
    { src: "/AMAROO/IMG_6893.webp", alt: "Zonas de silencio y contemplación - Desarrollo personal certificación coaching valores" },
    { src: "/AMAROO/IMG_6894.webp", alt: "Piscina y áreas de relajación en Amaroo - Bienestar integral retiro coaching Alicante" },
    { src: "/AMAROO/IMG_6895.webp", alt: "Senderos naturales para caminar y meditar - Experiencia holística certificación coaching" },
    { src: "/AMAROO/IMG_6897.webp", alt: "Atardeceres inspiradores en el centro Amaroo - Momentos de transformación coaching valores" },
    { src: "/AMAROO/IMG_6898.webp", alt: "Rincones de paz y tranquilidad en Amaroo - Ambiente ideal para aprendizaje profundo" },
    { src: "/AMAROO/IMG_6899.webp", alt: "Centro Amaroo, tu santuario de transformación profesional - Certificación Coaching X Valores" }
  ];

  const afterSupport = [
    { number: "12", label: "meses de acompañamiento" },
    { number: "14", label: "webinars en vivo" },
    { number: "∞", label: "comunidad privada" },
    { number: "1:1", label: "mentorías incluidas" }
  ];

  // Move all hooks to top level
  const [headerRef, headerInView] = useInView({ once: true, rootMargin: '-80px' });
  const [carouselRef, carouselInView] = useInView({ once: true, rootMargin: '-80px' });
  const [storyRef, storyInView] = useInView({ once: true, rootMargin: '-80px' });
  const [postRef, postInView] = useInView({ once: true, rootMargin: '-80px' });
  const [ctaRef, ctaInView] = useInView({ once: true, rootMargin: '-80px' });
  const [supportRef, supportInView] = useInView({ once: true, rootMargin: '-80px' });

  const headerSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: headerInView ? 1 : 0 },
    config: { duration: 400 }
  });

  const carouselSpring = useSpring({
    opacity: carouselInView ? 1 : 0,
    transform: carouselInView ? 'scale(1)' : 'scale(0.95)',
    config: { duration: 400 }
  });

  const storySpring = useSpring({
    opacity: storyInView ? 1 : 0,
    transform: storyInView ? 'translateX(0px)' : 'translateX(20px)',
    config: { duration: 400 },
    delay: 100
  });

  const postSpring = useSpring({
    opacity: postInView ? 1 : 0,
    transform: postInView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 }
  });

  const ctaSpring = useSpring({
    opacity: ctaInView ? 1 : 0,
    config: { duration: 400 }
  });

  // After support animations
  const supportSprings = useSprings(
    afterSupport.length,
    afterSupport.map((_, index) => ({
      opacity: supportInView ? 1 : 0,
      transform: supportInView ? 'translateY(0px)' : 'translateY(20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Track when venue section comes into view
  useEffect(() => {
    if (carouselInView) {
      trackViewContent('Sección Lugar - Alicante');
    }
  }, [carouselInView]);

  return (
    <section id="venue" className="py-16 md:py-24 bg-gradient-to-b from-white to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Powerful headline */}
        <animated.div
          ref={headerRef}
          className="text-center mb-16"
          style={headerSpring}
        >
          <p className="text-lg text-[#97d700] font-bold uppercase tracking-wider mb-4">
            No es un curso. Es una experiencia.
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black mb-6">
            3 días que no olvidarás
            <span className="block text-[#97d700]">en tu vida</span>
          </h2>
          <p className="text-xl md:text-2xl text-black/70 max-w-3xl mx-auto">
            Porque las transformaciones profundas no suceden en aulas con fluorescentes
          </p>
        </animated.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Image carousel */}
          <animated.div
            ref={carouselRef}
            className="h-[300px] sm:h-[400px] lg:h-[500px]"
            style={carouselSpring}
          >
            <ImageCarousel images={images} autoPlayInterval={4000} />
          </animated.div>

          {/* Venue story */}
          <animated.div
            ref={storyRef}
            className="space-y-6"
            style={storySpring}
          >
            <div className="bg-black text-white p-6 rounded-2xl">
              <p className="text-xl font-heading uppercase tracking-wider mb-2">
                Imagínate esto:
              </p>
              <p className="text-lg text-gray-300">
                Despiertas con el canto de los pájaros. Desayunas mirando las montañas.
                Aprendes rodeado de naturaleza. Practicas con personas que vibran como tú.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-[#97d700]/20">
              <h3 className="text-2xl md:text-3xl font-heading uppercase tracking-tight text-black mb-4">
                <a
                  href="https://maps.app.goo.gl/PZTjptpVaasBmMVc9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#97d700] transition-colors cursor-pointer flex items-center gap-2 justify-center"
                >
                  AMAROO, ALICANTE
                  <MapPin className="w-7 h-7 text-[#97d700]" />
                </a>
              </h3>
              <p className="text-gray-700 mb-4">
                El único centro de retiros en España elegido para esta certificación exclusiva
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-lg font-bold text-black">
                <div className="flex items-center gap-1">
                  <Waves className="w-5 h-5 text-[#97d700]" />
                  <span>20 min del mar</span>
                </div>
                <span className="text-gray-300 hidden md:inline">·</span>
                <div className="flex items-center gap-1">
                  <Trees className="w-5 h-5 text-[#97d700]" />
                  <span>10 hectáreas de naturaleza</span>
                </div>
                <span className="text-gray-300 hidden md:inline">·</span>
                <div className="flex items-center gap-1">
                  <Volume2 className="w-5 h-5 text-[#97d700]" />
                  <span>Silencio total</span>
                </div>
              </div>
            </div>

          </animated.div>
        </div>

        {/* Post support section */}
        <animated.div
          ref={postRef}
          className="bg-gradient-to-r from-black to-gray-900 text-white p-8 md:p-16 rounded-3xl relative overflow-hidden"
          style={postSpring}
        >
          <div className="relative max-w-4xl mx-auto text-center">
            <p className="text-[#97d700] font-bold uppercase tracking-wider mb-4">
              Pero aquí viene lo mejor
            </p>
            <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-tight mb-8">
              El retiro es solo el principio
            </h3>

            <div ref={supportRef} className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {supportSprings.map((style, index) => {
                const item = afterSupport[index];
                return (
                  <animated.div
                    key={index}
                    style={style}
                  >
                    <p className="text-4xl font-heading text-[#97d700] mb-2">{item.number}</p>
                    <p className="text-sm text-gray-400">{item.label}</p>
                  </animated.div>
                );
              })}
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl mb-8">
              <p className="text-2xl font-heading uppercase tracking-wider mb-4">
                Tu ecosistema de crecimiento continuo
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-lg text-gray-300">
                <div className="flex items-center gap-2">
                  <BookOpenCheck className="w-5 h-5 text-[#97d700]" />
                  <span>Campus Virtual</span>
                </div>
                <span className="text-gray-500 hidden md:inline">·</span>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#97d700]" />
                  <span>Comunidad 24/7</span>
                </div>
                <span className="text-gray-500 hidden md:inline">·</span>
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#97d700]" />
                  <span>Sesiones cada semana</span>
                </div>
              </div>
            </div>

            <p className="text-2xl font-bold text-[#97d700]">
              &ldquo;No te voy a soltar hasta que brilles&rdquo;
            </p>
            <p className="text-lg text-gray-400">- Toño Galán</p>
          </div>
        </animated.div>

        {/* Final CTA */}
        <animated.div
          ref={ctaRef}
          className="text-center mt-16"
          style={ctaSpring}
        >
          <p className="text-2xl md:text-3xl font-heading uppercase tracking-wider text-black mb-6">
            ¿Te imaginas siendo parte de esto?
          </p>
          <a
            href="#registration"
            className="inline-block px-10 py-5 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold text-xl rounded-full shadow-2xl hover:shadow-[#97d700]/30 transform hover:scale-105 transition-all duration-300"
          >
            SOLICITAR MI LUGAR
          </a>
        </animated.div>
      </div>
    </section>
  );
}
